import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import React, { useContext, useRef, useEffect } from 'react';
import { Enter, Space, ArrowLeft, ArrowDown, ArrowRight, ArrowUp } from '../../utils/keycode.js';
import { TabsContext } from './context.js';
import defaultStyles from './styles/Tabs.module.scss.js';
import { focusPreviousSiblingOf, focusNextSiblingOf } from './util/focus.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { Typography } from '../typography/Typography.js';

const TabHeader = /*#__PURE__*/React.forwardRef((props, ref) => {
  const ctxProps = useContext(TabsContext);
  const mergeProps = {
    ...props,
    ...ctxProps
  };
  const animatedLineRef = useRef();
  const titleRef = useRef({});
  const {
    panelChildren,
    direction,
    type = 'line',
    activeTab,
    size = 'md',
    tabPosition,
    theme,
    extra
  } = mergeProps;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  useEffect(() => {
    if (titleRef?.current && animatedLineRef?.current) {
      const line = animatedLineRef.current;
      line.style.left = '';
      line.style.top = '';
      line.style.maxWidth = '';
      line.style.maxHeight = '';
      const activeTitle = titleRef?.current[activeTab];
      const elementBounds = activeTitle?.getBoundingClientRect();
      const left = activeTitle?.offsetLeft;
      const top = activeTitle?.offsetTop;
      const width = elementBounds?.width;
      const height = elementBounds?.height;
      if (tabPosition === 'left' || tabPosition === 'right') {
        line.style.top = `${top}px`;
        line.style.maxHeight = `${height}px`;
      } else {
        line.style.left = `${left}px`;
        line.style.maxWidth = `${width}px`;
      }
    }
  }, [animatedLineRef, activeTab, tabPosition]);
  const handleKeyDown = (event, key) => {
    const keyCode = event.key;
    if (keyCode === Enter.key || keyCode === Space.key) {
      event.preventDefault();
      mergeProps.onClickTab(key);
    }
    const focusPrevious = event => {
      event.preventDefault();
      focusPreviousSiblingOf(event.target);
    };
    const focusNext = event => {
      event.preventDefault();
      focusNextSiblingOf(event.target);
    };
    if (event.target instanceof HTMLElement) {
      switch (keyCode) {
        case ArrowUp.key:
          focusPrevious(event);
          break;
        case ArrowRight.key:
        case ArrowDown.key:
          focusNext(event);
          break;
        case ArrowLeft.key:
          focusPrevious(event);
          break;
        //
      }
    }
  };

  if (!panelChildren) return null;
  return /*#__PURE__*/jsxs("div", {
    className: clsx(styles.tabslist, direction === 'horizontal' && styles.horizontal, direction === 'vertical' && styles.vertical, tabPosition === 'top' && styles.top, tabPosition === 'left' && styles.left, tabPosition === 'right' && styles.right, size === 'xs' && styles.xs, size === 'sm' && styles.sm, size === 'md' && styles.md, size === 'lg' && styles.lg, type === 'line' && styles.line, type === 'card' && styles.card, type === 'card-gutter' && styles.card_gutter),
    ref: ref,
    children: [/*#__PURE__*/jsxs("div", {
      className: styles.tabslist_wrap,
      role: "tablist",
      children: [panelChildren?.map(child => /*#__PURE__*/jsx("button", {
        role: "tab",
        "data-active": activeTab === child.key,
        type: "button",
        tabIndex: child.props.disabled ? -1 : 0,
        ref: node => {
          titleRef.current[child.key] = node;
        },
        disabled: child.props.disabled,
        className: clsx(styles.title, {
          [styles.title_active]: activeTab === child.key,
          [styles.title_disabled]: child.props.disabled
        }),
        onClick: () => {
          if (child.props.disabled) return;
          mergeProps.onClickTab && mergeProps.onClickTab(child.key);
        },
        onKeyDown: event => handleKeyDown(event, child.key),
        children: /*#__PURE__*/jsx(Typography, {
          children: child.props.title
        })
      }, child.key)), type === 'line' && /*#__PURE__*/jsx("div", {
        ref: animatedLineRef,
        className: styles.animated_line
      })]
    }), extra && /*#__PURE__*/jsx("div", {
      className: styles.tabs_header_extra,
      children: extra
    })]
  });
});
TabHeader.displayName = 'TabHeader';

export { TabHeader };
